import React from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import * as Yup from 'yup';
import { Button, Row, Col } from 'react-bootstrap';
import { FormikInput, FormikCheckBox, UploadFile } from '../../components';

const RiskPreventionDocumentForm = ({
  values,
  errors,
  touched,
  isSubmitting,
  setFieldValue,
  fieldName = 'riskPrevention'
}) => {
  const fieldPath = `${fieldName}`;

  return (
    <Form>
      <Row>
        <Col md={12}>
          <Field name={`${fieldPath}[name]`}>
            {({ field }) => (
              <FormikInput
                {...field}
                abbr
                label="Nombre del documento"
                disabled={isSubmitting}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={12}>
          <div className="mb-2">Archivo</div>
          <Field name={`${fieldPath}[document]`}>
            {({ field }) => (
              <>
                <UploadFile
                  {...field}
                  name={values[fieldName]?.document?.name || 'Adjuntar Archivo'}
                  onChange={file => {
                    setFieldValue(field.name, file || null);
                    if (file && !values[fieldName].name?.trim()) {
                      const baseName = file.name.replace(/\.[^/.]+$/, '');
                      setFieldValue(`${fieldPath}[name]`, baseName);
                    }
                  }}
                  error={getIn(touched, field.name) && getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                  formats={['.pdf', '.doc', '.docx']}
                  tooltipText="Formatos permitidos (DOC, DOCX, PDF). Máx 10MB."
                />
                {getIn(touched, field.name) && getIn(errors, field.name) && (
                  <div className="invalid-feedback d-block">{getIn(errors, field.name)}</div>
                )}
              </>
            )}
          </Field>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={12}>
          <Field name={`${fieldPath}[isPublic]`}>
            {({ field }) => (
              <FormikCheckBox
                {...field}
                field={field}
                label="Documento Público"
                custom
                type="switch"
                disabled={isSubmitting}
              />
            )}
          </Field>
        </Col>
      </Row>

      <div className="d-flex justify-content-end mt-4">
        <Button type="submit" variant="primary">
          Guardar
        </Button>
      </div>
    </Form>
  );
};

const SUPPORTED_FORMATS = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const validationSchema = (action, fieldName = 'riskPrevention') =>
  Yup.object().shape({
    [fieldName]: Yup.object().shape({
      name: Yup.string()
        .trim()
        .required('Ingresa un nombre.'),
      document:
        action === 'new'
          ? Yup.mixed()
              .required('Debes adjuntar un archivo.')
              .test('FILE_SIZE', 'El archivo excede 10MB.', file => !!file && file.size <= 10 * 1024 * 1024)
              .test('FILE_FORMAT', 'Formato no compatible.', file => !!file && SUPPORTED_FORMATS.includes(file.type))
          : Yup.mixed()
              .nullable()
              .test('FILE_SIZE', 'El archivo excede 10MB.', file => !file || file.size <= 10 * 1024 * 1024)
              .test('FILE_FORMAT', 'Formato no compatible.', file => !file || SUPPORTED_FORMATS.includes(file.type)),
      isPublic: Yup.boolean()
    })
  });

const setInitialValues = (props, fieldName = 'riskPrevention') => ({
  id: props.id || null,
  [fieldName]: {
    name: props.name || '',
    document: null,
    isPublic: props.isPublic || false
  }
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest } = props;
  formRequest(values, setSubmitting);
};

export default withFormik({
  mapPropsToValues: props => setInitialValues(props, props.fieldName || 'riskPrevention'),
  validationSchema: props => validationSchema(props.action, props.fieldName || 'riskPrevention'),
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: props => props.action !== 'new'
})(RiskPreventionDocumentForm);
